/**
 * 🎓 Phase 15.1 — Help Center Modal
 *
 * Permanent home for the 10 tutorial sections. Each section shows
 * status (✅ completed / 🔵 in progress / ⚪ not started / 🔒 coming
 * soon for unshipped sections). Replay buttons re-walk a completed
 * section; Start buttons launch a never-opened one.
 *
 * Open via:
 *   - FloatingHelpButton (bottom-right ?) — always visible to authed users
 *   - Profile dropdown entry → "Help & Tutorials"
 *   - /help route (also resolves to SupportPage by default; this modal
 *     is opt-in via prop)
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useTutorial } from "@/components/tutorial/TutorialProvider";
import { hasShippedContent } from "@/components/tutorial/sectionContent";

const StatusPill = ({ kind }) => {
  const map = {
    completed:  { label: "Completed",   cls: "bg-emerald-500/15 border-emerald-400/40 text-emerald-200" },
    in_progress:{ label: "In progress", cls: "bg-amber-500/15 border-amber-400/40 text-amber-200" },
    not_started:{ label: "Not started", cls: "bg-white/5 border-white/15 text-gray-300" },
    coming_soon:{ label: "Phase 15.2",  cls: "bg-purple-500/15 border-purple-400/40 text-purple-200" },
  }[kind] || { label: "—", cls: "" };
  return (
    <span className={`text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded-full border ${map.cls}`}>
      {map.label}
    </span>
  );
};

const HelpCenterModal = ({ open, onOpenChange }) => {
  const {
    progress, catalog, criticalIds, activeSection,
    startSection, replaySection, skipAll,
  } = useTutorial();

  const completed = new Set(progress?.completed_sections || []);
  const inProgress = activeSection;

  const sectionStatus = (id) => {
    if (!hasShippedContent(id)) return "coming_soon";
    if (completed.has(id)) return "completed";
    if (inProgress === id) return "in_progress";
    return "not_started";
  };

  const handleStart = (id) => {
    onOpenChange(false);
    setTimeout(() => startSection(id, { source: "help_center" }), 50);
  };
  const handleReplay = (id) => {
    onOpenChange(false);
    setTimeout(() => replaySection(id), 50);
  };

  const totalCritical = (criticalIds || []).length || 4;
  const completedCritical = (criticalIds || []).filter((id) => completed.has(id)).length;
  const pct = totalCritical ? Math.round((completedCritical / totalCritical) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="help-center-modal"
        className="bg-gradient-to-br from-[#181029] via-[#0d0717] to-[#0a0514] border-amber-400/30 text-white max-w-2xl max-h-[88vh] p-0 overflow-hidden flex flex-col"
      >
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-amber-500/20 shrink-0">
          <DialogTitle className="text-lg flex items-center gap-2">
            <span>🎓</span>
            <span>Tutorial Center</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-400 mt-1">
            Bite-sized walkthroughs for every part of RGM Studio. Pick a section, hit start, and we'll guide you step by step.
          </DialogDescription>
          {progress && (
            <div className="mt-3" data-testid="help-center-progress">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider font-black text-amber-300/80">
                  Onboarding progress
                </span>
                <span className="text-xs font-black text-white">
                  {completedCritical}/{totalCritical} core sections · {pct}%
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-black/40 overflow-hidden border border-white/5">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-pink-400 transition-[width] duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
          {(catalog || []).map((s) => {
            const status = sectionStatus(s.id);
            const isCritical = s.critical;
            return (
              <div
                key={s.id}
                data-testid={`help-section-${s.id}`}
                className="flex items-center gap-3 rounded-xl border border-amber-500/15 bg-black/30 px-3 py-3 hover:border-amber-400/40 transition-colors"
              >
                <span className="text-2xl shrink-0">{s.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-bold text-sm">{s.title}</span>
                    {isCritical && (
                      <span className="text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/35 text-amber-200">
                        Core
                      </span>
                    )}
                    <StatusPill kind={status} />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{s.blurb}</p>
                </div>
                {status === "coming_soon" ? (
                  <button
                    disabled
                    className="text-[11px] font-bold px-3 py-1.5 rounded-full border border-white/10 text-gray-500 cursor-not-allowed shrink-0"
                  >
                    Soon
                  </button>
                ) : status === "completed" ? (
                  <button
                    onClick={() => handleReplay(s.id)}
                    data-testid={`help-section-replay-${s.id}`}
                    className="text-[11px] font-bold px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 shrink-0"
                  >
                    Replay
                  </button>
                ) : (
                  <button
                    onClick={() => handleStart(s.id)}
                    data-testid={`help-section-start-${s.id}`}
                    className="text-[11px] font-black px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white shadow-md shadow-amber-500/30 shrink-0"
                  >
                    {status === "in_progress" ? "Resume" : "Start"} →
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-amber-500/15 shrink-0 flex items-center justify-between text-[11px] text-gray-500">
          <span>Tap any section to start. Skip never-show-again disables auto-launch.</span>
          {!progress?.skipped && (
            <button
              onClick={() => { onOpenChange(false); skipAll(); }}
              data-testid="help-center-skip-all"
              className="text-gray-400 hover:text-white underline-offset-4 hover:underline"
            >
              Never show automatically
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpCenterModal;
